import { Controller } from "@hotwired/stimulus"
import { chooseZipAction, uploadResponse } from "../zip_upload"

export default class extends Controller {
  static targets = ["overlay", "fileInput", "hiddenInput", "uploadQueue"]
  static values = {
    url: String
  }

  connect() {
    this.#dragCounter = 0
    this.#uploadIdCounter = 0
  }

  // Drag enter on the zone
  dragenter(event) {
    // Only handle external file drops, not internal row drags
    if (!this.#isFileDrag(event)) return

    event.preventDefault()
    event.stopPropagation()
    this.#dragCounter++
    if (this.#dragCounter === 1) {
      this.#showOverlay()
    }
  }

  // Drag over keeps the overlay visible
  dragover(event) {
    // Only handle external file drops, not internal row drags
    if (!this.#isFileDrag(event)) return

    event.preventDefault()
    event.stopPropagation()
  }

  // Drag leave hides overlay when fully exited
  dragleave(event) {
    // Only handle external file drops, not internal row drags
    if (!this.#isFileDrag(event)) return

    event.preventDefault()
    event.stopPropagation()
    this.#dragCounter--
    if (this.#dragCounter === 0) {
      this.#hideOverlay()
    }
  }

  // Drop handles file upload
  drop(event) {
    // Only handle external file drops, not internal row drags
    if (!this.#isFileDrag(event)) return

    event.preventDefault()
    event.stopPropagation()
    this.#dragCounter = 0
    this.#hideOverlay()

    const files = Array.from(event.dataTransfer.files)
    if (files.length > 0) {
      this.#uploadFiles(files)
    }
  }

  // Handle file input change
  handleFiles(event) {
    const files = Array.from(event.target.files)
    if (files.length > 0) {
      this.#uploadFiles(files)
    }
    // Reset input so same file can be selected again
    event.target.value = ""
  }

  // Private methods

  #dragCounter = 0
  #uploadIdCounter = 0

  // Check if this is an external file drag (not internal row reorder)
  #isFileDrag(event) {
    // dataTransfer.types includes "Files" for external file drags
    // Internal drags will have "text/plain" but not "Files"
    return event.dataTransfer.types.includes("Files")
  }

  #showOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("hidden")
    }
  }

  #hideOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("hidden")
    }
  }

  async #uploadFiles(files) {
    for (const file of files) {
      const zipAction = await chooseZipAction(file)
      if (zipAction) this.#uploadFile(file, zipAction)
    }
  }

  #uploadFile(file, zipAction) {
    const uploadId = this.#generateId()
    this.#addToQueue(uploadId, file)

    const formData = new FormData()
    formData.append("asset[original_file]", file)
    formData.append("zip_action", zipAction)

    const xhr = new XMLHttpRequest()

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded / event.total) * 100)
        this.#updateProgress(uploadId, percent)
      }
    })

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const response = uploadResponse(xhr)
        if (response.import_url) {
          this.#showImportStatus(uploadId, response.import_url)
        } else {
          this.#removeFromQueue(uploadId)
        }
      } else {
        console.error("Upload failed:", xhr.status)
        this.#markError(uploadId, uploadResponse(xhr).error)
      }
    })

    xhr.addEventListener("error", () => {
      console.error("Network error during upload")
      this.#markError(uploadId)
    })

    xhr.open("POST", this.urlValue)
    xhr.setRequestHeader("X-CSRF-Token", this.#getCsrfToken())
    xhr.setRequestHeader("Accept", "application/json")
    xhr.send(formData)
  }

  #generateId() {
    return `upload_${++this.#uploadIdCounter}_${Date.now()}`
  }

  #addToQueue(uploadId, file) {
    if (!this.hasUploadQueueTarget) return

    const element = this.#createPreviewElement(uploadId, file)
    this.uploadQueueTarget.appendChild(element)
    this.uploadQueueTarget.classList.remove("hidden")
  }

  #createPreviewElement(uploadId, file) {
    const div = document.createElement("div")
    div.id = uploadId
    div.className = "flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm border"

    const isImage = file.type.startsWith("image/")
    let previewContent

    if (isImage) {
      const objectUrl = URL.createObjectURL(file)
      previewContent = `<img src="${objectUrl}" class="w-full h-full object-cover" onload="URL.revokeObjectURL(this.src)" />`
    } else {
      previewContent = `<i class="fadr fa-file text-gray-400 text-lg"></i>`
    }

    div.innerHTML = `
      <div class="w-10 h-10 bg-gray-100 rounded overflow-hidden flex items-center justify-center shrink-0">
        ${previewContent}
      </div>
      <div class="flex-1 min-w-0">
        <div class="text-sm font-medium truncate">${this.#escapeHtml(file.name)}</div>
        <div class="w-full bg-gray-200 rounded-full h-1.5 mt-1">
          <div class="bg-blue-500 h-1.5 rounded-full transition-all duration-150" style="width: 0%" data-progress-bar></div>
        </div>
      </div>
      <span class="text-xs text-gray-500 w-8 text-right" data-progress-text>0%</span>
    `

    return div
  }

  #updateProgress(uploadId, percent) {
    const element = document.getElementById(uploadId)
    if (!element) return

    const progressBar = element.querySelector("[data-progress-bar]")
    const progressText = element.querySelector("[data-progress-text]")

    if (progressBar) progressBar.style.width = `${percent}%`
    if (progressText) progressText.textContent = percent === 100 ? "Importing…" : `${percent}%`
  }

  #removeFromQueue(uploadId) {
    const element = document.getElementById(uploadId)
    if (!element) return

    element.classList.add("opacity-0", "transition-opacity", "duration-300")
    setTimeout(() => {
      element.remove()
      this.#hideQueueIfEmpty()
    }, 300)
  }

  #showImportStatus(uploadId, url) {
    const element = document.getElementById(uploadId)
    if (!element) return

    element.querySelector("[data-progress-text]").textContent = "Queued"
    const link = document.createElement("a")
    link.href = url
    link.className = "text-sm underline"
    link.textContent = "View import status"
    element.appendChild(link)
  }

  #markError(uploadId, message = "Upload failed. Please try again.") {
    const element = document.getElementById(uploadId)
    if (!element) return

    const progressBar = element.querySelector("[data-progress-bar]")
    const progressText = element.querySelector("[data-progress-text]")

    if (progressBar) {
      progressBar.classList.remove("bg-blue-500")
      progressBar.classList.add("bg-red-500")
    }
    if (progressText) {
      progressText.textContent = "Failed"
      progressText.classList.add("text-red-500")
    }

    element.classList.add("border-red-200", "bg-red-50")

    const error = document.createElement("p")
    error.className = "text-sm text-red-700"
    error.textContent = message
    element.appendChild(error)
    const dismiss = document.createElement("button")
    dismiss.type = "button"
    dismiss.className = "btn btn-ghost btn-xs"
    dismiss.textContent = "Dismiss"
    dismiss.addEventListener("click", () => this.#removeFromQueue(uploadId))
    element.appendChild(dismiss)
  }

  #hideQueueIfEmpty() {
    if (!this.hasUploadQueueTarget) return
    if (this.uploadQueueTarget.children.length === 0) {
      this.uploadQueueTarget.classList.add("hidden")
    }
  }

  #escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  #getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
